import { createSharedComposable } from '@vueuse/core';
import { computed, ref } from 'vue';
import type {
  DisplayContent,
  ResponseTextContent,
  StepDisplayContent,
  StreamMessage,
} from '../types/message.types';
import type {
  StepContent,
  StepContentText,
  StepContentTool,
  StepContentType,
} from '../types/step-content.types';
import { useConversationLayout } from '@/components/common/layout/use-conversation-layout';
import { useShareConversation } from './use-share-conversation';
import { useTracking } from '@/composables/tracking/use-tracking';
import { ReverseEngineeringApi } from '@/domains/material/apis/re-api';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { useRoute, useRouter } from 'vue-router';
import type { PsHttpSSE } from '@patsnap-utils/sse';

export const useConversation = createSharedComposable(() => {
  const apiService = injectService<ReverseEngineeringApi>(ReverseEngineeringApi);
  const router = useRouter();
  const route = useRoute();
  const { sendTracking } = useTracking()

  // share
  const { isViewingShared } = useShareConversation()

  // conversation layout controls
  const { isDetailExpanded, toggleDetail } = useConversationLayout();

  const isChatting = ref(false);
  const isConnecting = ref(false);
  const isError = ref(false);

  const conversationDetails = ref<Record<string, any>>({});

  const conversationId = computed(() => {
    if (route.params.conversation_id && typeof route.params.conversation_id === 'string') {
      return route.params.conversation_id;
    }

    return '';
  });

  const conversationStream = ref<PsHttpSSE<Record<any, any>>>();

  const msgIds = ref<string[]>([]); // ordered for display
  const msgIdsSet = ref<Set<string>>(new Set([])); // used for checking

  const isConversationEmpty = computed(() => {
    return msgIds.value.length === 0;
  });

  const content = ref<Record<string, DisplayContent>>({}); // k: content_id, v: display content
  const contentTypeMap = ref<Record<string, string>>({}); // k: content_id, v: content type

  const seenStepContentIds = ref(new Set()); // to prevent rerenders
  const lastSeenMsgOrder = ref(-1); // to prevent appending same messages

  const openedDetailMeta = ref<{
    contentId: string;
    stepContentId: string;
  }>();

  const setOpenedDetailMeta = (contentId: string, stepContentId: string) => {
    openedDetailMeta.value = {
      contentId,
      stepContentId,
    };
  };

  const openedDetail = computed<StepContentTool | undefined>(() => {
    if (openedDetailMeta.value?.contentId && openedDetailMeta.value?.stepContentId) {
      return (content.value[openedDetailMeta.value.contentId] as StepDisplayContent).stepContent[
        openedDetailMeta.value.stepContentId
      ] as StepContentTool;
    } else {
      return undefined;
    }
  });

  const clearOpenedDetail = () => {
    openedDetailMeta.value = {
      contentId: '',
      stepContentId: '',
    };
  };

  const resetConversation = () => {
    isError.value = false;
    lastSeenMsgOrder.value = -1;
    conversationDetails.value = {};
    msgIds.value = [];
    msgIdsSet.value = new Set([]);
    content.value = {};
    contentTypeMap.value = {};
    clearOpenedDetail();
    conversationStream.value?.close();
  };

  const onReceiveStepMessage = (
    contentData:
      | StepContent<'text', StepContentText>
      | StepContent<'tool_call' | 'tool_result', StepContentTool>,
    stepData: StepDisplayContent,
    contentId: string,
  ) => {
    const res: StepDisplayContent = {
      stepTitle: contentData.step_title,
      stepStatus: contentData.step_status,
      stepContentIds: [...stepData.stepContentIds],
      stepContentIdsSet: new Set([...stepData.stepContentIds]),
      stepContent: { ...stepData.stepContent },
      stepContentTypeMap: { ...stepData.stepContentTypeMap },
    };

    res.stepTitle = contentData.step_title ? contentData.step_title : res.stepTitle || ('' as any);
    res.stepStatus = contentData.step_status
      ? contentData.step_status
      : res.stepStatus || ('PROCESSING' as any);

    const pushNewStepContentId = (id: string, contentType: StepContentType) => {
      res.stepContentIds.push(id);
      res.stepContentIdsSet.add(id);
      res.stepContentTypeMap[id] = contentType;
    };

    switch (contentData.step_content_type) {
      case 'text':
        if (!res.stepContentIdsSet.has(contentData.step_content_id)) {
          pushNewStepContentId(contentData.step_content_id, contentData.step_content_type);
          res.stepContent[contentData.step_content_id] = contentData.step_content_data;
        } else {
          const stepContent = res.stepContent[contentData.step_content_id] as StepContentText;
          stepContent.text += contentData.step_content_data.text;
        }
        break;
      case 'tool_call':
        if (!res.stepContentIdsSet.has(contentData.step_content_id)) {
          pushNewStepContentId(contentData.step_content_id, contentData.step_content_type);
          res.stepContent[contentData.step_content_id] = contentData.step_content_data;

          // open new detail when no expansion
          if (!isDetailExpanded.value) {
            setOpenedDetailMeta(contentId, contentData.step_content_id);
            toggleDetail(true);
          }
        }
        break;
      case 'tool_result':
        if (res.stepContentIdsSet.has(contentData.step_content_id)) {
          res.stepContent[contentData.step_content_id] = contentData.step_content_data;
        }
        break;
    }

    return res;
  };

  const pushNewMsgId = (id: string, contentType: string) => {
    msgIds.value.push(id);
    msgIdsSet.value.add(id);
    contentTypeMap.value[id] = contentType;
  };

  const onMessage = (latestMsg: StreamMessage) => {
    if (!latestMsg) {
      return;
    }

    if (lastSeenMsgOrder.value >= latestMsg.msg_order) {
      return;
    }

    lastSeenMsgOrder.value = latestMsg.msg_order;

    switch (latestMsg.content_type) {
      case 'conversation_details':
        conversationDetails.value = latestMsg.content_data;
        break;
      case 'user_query':
        content.value[latestMsg.content_id] = latestMsg.content_data;
        pushNewMsgId(latestMsg.content_id, latestMsg.content_type);
        break;
      case 'response_text':
        if (!msgIdsSet.value.has(latestMsg.content_id)) {
          pushNewMsgId(latestMsg.content_id, latestMsg.content_type);
          content.value[latestMsg.content_id] = latestMsg.content_data;
        } else {
          (content.value[latestMsg.content_id] as ResponseTextContent).text +=
            latestMsg.content_data.text;
        }
        break;
      case 'step_content':
        if (!msgIdsSet.value.has(latestMsg.content_id)) {
          pushNewMsgId(latestMsg.content_id, latestMsg.content_type);
          content.value[latestMsg.content_id] = onReceiveStepMessage(
            latestMsg.content_data,
            {
              stepTitle: latestMsg.content_data.step_title,
              stepStatus: latestMsg.content_data.step_status,
              stepContentIds: [],
              stepContentIdsSet: new Set([]),
              stepContent: {},
              stepContentTypeMap: {},
            },
            latestMsg.content_id,
          );
        } else {
          content.value[latestMsg.content_id] = onReceiveStepMessage(
            latestMsg.content_data,
            content.value[latestMsg.content_id] as StepDisplayContent,
            latestMsg.content_id,
          );
        }
        break;
      case 'error':
        isError.value = true;
        break;
    }
  };

  const createConversation = async (query: string) => {
    isChatting.value = true;
    const res = await apiService.chat({
      query,
      files: [],
      tool_input: {},
      conversation_id: '',
      sub_agent_name: '',
    });
    isChatting.value = false;

    if (res && res.conversation_id) {

      sendTracking('ss_mse_reverse_engineering', 'create_task')

      router.push({
        name: 'ReverseEngineeringConversation',
        params: { conversation_id: res.conversation_id },
      });
    }
  };

  const connectConversation = async () => {
    // if (conversationStream.value) {
    //   conversationStream.value.close();
    // }

    const stream = isViewingShared.value ? apiService.connectShare(conversationId.value) : apiService.connect(conversationId.value);
    conversationStream.value = stream;

    isConnecting.value = true;

    stream.start();

    stream.onMessage((m) => {
      onMessage(m as any as StreamMessage);
    });

    stream.onClose(() => {
      isConnecting.value = false;
    });
  };

  const replyConversation = async (toolInput: Record<string, any>) => {
    isChatting.value = true;
    const chatInput = {
      query: '',
      files: [],
      tool_input: toolInput,
      conversation_id: conversationId.value,
      sub_agent_name: '',
    };
    await apiService.chat(chatInput);
    isChatting.value = false;
  };

  const chatConversation = async (query: string) => {
    isChatting.value = true;
    const chatInput = {
      query,
      files: [],
      tool_input: {},
      conversation_id: conversationId.value,
      sub_agent_name: '',
    };
    await apiService.chat(chatInput);
    isChatting.value = false;
  };

  return {
    // state variables
    isError,
    isChatting,
    isConnecting,
    isConversationEmpty,

    // conversation details and messages
    conversationDetails,
    conversationId,
    msgIds,
    contentTypeMap,
    content,

    // resetting conversation
    resetConversation,

    // right pane detail related methods and variables
    setOpenedDetailMeta,
    clearOpenedDetail,
    openedDetail,
    openedDetailMeta,
    seenStepContentIds,

    // api methods
    createConversation,
    connectConversation,
    replyConversation,
    chatConversation,
  };
});
